import React, { useEffect, useState } from 'react';
import { XIcon } from 'lucide-react';
interface CurrencyConfig {
  currency: string;
  exchangeRatio: number;
  safetyMargin: number;
}
interface CurrencyChangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentCurrency: string;
  onApply: (config: CurrencyConfig) => void;
  title?: string;
}
export function CurrencyChangeModal({
  isOpen,
  onClose,
  currentCurrency,
  onApply,
  title = 'Change currency'
}: CurrencyChangeModalProps) {
  const [selectedCurrency, setSelectedCurrency] = useState(currentCurrency);
  const [exchangeRatio, setExchangeRatio] = useState(1);
  const [safetyMargin, setSafetyMargin] = useState(0);
  useEffect(() => {
    if (isOpen) {
      setSelectedCurrency(currentCurrency);
      setExchangeRatio(1);
      setSafetyMargin(0);
    }
  }, [isOpen, currentCurrency]);
  if (!isOpen) return null;
  const currencies = [{
    code: 'USD',
    symbol: '$',
    name: 'US Dollar'
  }, {
    code: 'EUR',
    symbol: '€',
    name: 'Euro'
  }, {
    code: 'GBP',
    symbol: '£',
    name: 'British Pound'
  }, {
    code: 'MXN',
    symbol: '$',
    name: 'Mexican Peso'
  }, {
    code: 'SAR',
    symbol: 'ر.س',
    name: 'Saudi Riyal'
  }];
  const selectedCurrencyData = currencies.find(c => c.code === selectedCurrency);
  const handleApply = () => {
    onApply({
      currency: selectedCurrency,
      exchangeRatio,
      safetyMargin
    });
    onClose();
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-232">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-id="element-233">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200" data-id="element-234">
          <h2 className="text-xl font-bold text-gray-900" data-id="element-235">{title}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-236">
            <XIcon className="w-5 h-5 text-gray-500" data-id="element-237" />
          </button>
        </div>
        {/* Content */}
        <div className="p-6" data-id="element-238">
          <div className="grid grid-cols-2 gap-8" data-id="element-239">
            {/* Left Column */}
            <div className="space-y-6" data-id="element-240">
              {/* Select Currency */}
              <div data-id="element-241">
                <label className="block text-sm font-semibold text-gray-900 mb-2" data-id="element-242">
                  Select currency
                </label>
                <div className="relative" data-id="element-243">
                  <select value={selectedCurrency} onChange={e => setSelectedCurrency(e.target.value)} className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-300 rounded-lg appearance-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-gray-900" data-id="element-244">
                    {currencies.map(currency => <option key={currency.code} value={currency.code} data-id="element-245">
                        {currency.symbol} {currency.code}
                      </option>)}
                  </select>
                </div>
              </div>
              {/* Exchange Ratio */}
              <div data-id="element-246">
                <label className="block text-sm font-semibold text-gray-900 mb-2" data-id="element-247">
                  Exchange ratio
                </label>
                <input type="number" min="0" step="0.0001" value={exchangeRatio} onChange={e => setExchangeRatio(Number(e.target.value))} className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-gray-900" data-id="element-248" />
              </div>
              {/* Safety Margin */}
              <div data-id="element-249">
                <label className="block text-sm font-semibold text-gray-900 mb-2" data-id="element-250">
                  Safety margin (Optional)
                </label>
                <div className="relative" data-id="element-251">
                  <input type="number" min="0" max="100" step="0.1" value={safetyMargin} onChange={e => setSafetyMargin(Number(e.target.value))} className="w-full px-4 py-3 pr-10 bg-gray-50 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-gray-900" data-id="element-252" />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium" data-id="element-253">
                    %
                  </span>
                </div>
              </div>
            </div>
            {/* Right Column - Visual Representation */}
            <div className="flex items-center justify-center" data-id="element-254">
              <div className="text-center" data-id="element-255">
                <div className="mb-4 text-sm text-gray-600" data-id="element-256">
                  1 {currentCurrency} =
                </div>
                <div className="flex items-center justify-center gap-3 mb-2" data-id="element-257">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-lg" data-id="element-258">
                    <span className="text-2xl" data-id="element-259">💰</span>
                  </div>
                </div>
                <div className="text-4xl font-bold text-gray-900" data-id="element-260">
                  {exchangeRatio.toFixed(4)} {selectedCurrencyData?.code}
                </div>
                {safetyMargin > 0 && <div className="mt-3 text-sm text-gray-600" data-id="element-261">
                    + {safetyMargin}% safety margin
                  </div>}
              </div>
            </div>
          </div>
        </div>
        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-end gap-3" data-id="element-262">
          <button onClick={onClose} className="px-6 py-2.5 text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-all font-semibold" data-id="element-263">
            Cancel
          </button>
          <button onClick={handleApply} className="px-6 py-2.5 text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all font-semibold shadow-md" data-id="element-264">
            Accept and convert
          </button>
        </div>
      </div>
    </div>;
}